import { environment } from '../../../environments/environment';
import { createUrl } from '../../utils/utils';
import { TenantConfigResponse, WidgetBootContext } from './widget-config';

/**
 * What `main.ts` works out before Angular exists: which tenant this document is
 * for, whether it is framed, and what Product says about that tenant.
 *
 * Kept out of `main.ts` so it can be exercised without bootstrapping the app.
 */

// MUST stay in sync with the per-env redirect_uri patterns registered on
// ol-auth's oc_customer_web_v1 client (e.g.
// https://embed.open.limo/embed/dev/{tenant_slug}/* for the test env).
// Renaming or adding an env segment here without registering the matching
// ol-auth pattern silently breaks tenant resolution at /authorize.
const ENV_PATH_REGEX = /^\/embed\/(dev|test|staging|v1)\//;

const CONFIG_CACHE_TTL_MS = 24 * 60 * 60 * 1000;

/**
 * Path segment ol-auth redirects a top-level sign-in back to, under the slug:
 * `/embed/{env}/{slug}/auth-callback`.
 *
 * It cannot be the bare slug root. ol-auth expands a registered
 * `…/{tenant_slug}/*` pattern to `.+`, so the pattern matches only a URL with a
 * non-empty segment after the slug — the bare prefix never matches
 * (`redirect_uri_patterns.py`). CloudFront's SPA fallback serves index.html for
 * this path, the app boots, `AuthorizationService` completes the code exchange
 * and then rewrites the URL back to the slug root.
 */
export const AUTH_CALLBACK_SEGMENT = 'auth-callback';

/**
 * The parent window's origin when we are inside an iframe. Used as the
 * targetOrigin for postMessage to the operator's page (wheel forwarding).
 * Null when not framed, or when the parent's referrer is not exposed (strict
 * referrer policies).
 */
function detectParentOrigin(): string | null {
    if (window.parent === window) return null;
    if (!document.referrer) return null;
    try {
        return new URL(document.referrer).origin;
    } catch {
        return null;
    }
}

export function parseBootContext(path = window.location.pathname): WidgetBootContext {
    const envMatch = path.match(ENV_PATH_REGEX);
    const parentOrigin = detectParentOrigin();

    // Standalone /customer path (my-test.open.limo). No /embed/ prefix —
    // AppModule and AuthorizationService fall back to environment.defaultTenant.
    if (!envMatch) {
        return { envPath: '', slug: null, isEmbedded: false, inAppPath: '/', parentOrigin };
    }

    const envPath = envMatch[0];
    const afterEnv = path.slice(envPath.length);

    // /embed/<env>/ or /embed/<env>/index.html — no slug in the URL. Reached by
    // the post-logout landing: post_logout_redirect_uri is tenant-agnostic
    // because ol-auth /logout validates against the static post_logout_uris
    // list, not the templated pattern. main.ts recovers the slug from
    // sessionStorage there.
    if (afterEnv === '' || afterEnv === 'index.html') {
        return { envPath, slug: null, isEmbedded: false, inAppPath: '/', parentOrigin };
    }

    const segments = afterEnv.split('/').filter(Boolean);
    const slug = segments[0];
    const isEmbedded = segments[1] === 'iframe';
    // The OIDC landing is the tenant's home, not a route of its own: the app
    // boots at the slug root and the callback segment is rewritten away once
    // the code exchange is done.
    if (!isEmbedded && segments[1] === AUTH_CALLBACK_SEGMENT && segments.length === 2) {
        return { envPath, slug, isEmbedded: false, inAppPath: '/', parentOrigin };
    }
    const inAppSegments = segments.slice(isEmbedded ? 2 : 1);
    const inAppPath = inAppSegments.length === 0 ? '/' : '/' + inAppSegments.join('/');
    return { envPath, slug, isEmbedded, inAppPath, parentOrigin };
}

/** Class stamped on `<html>` while the app is running inside an operator's page. */
export const EMBEDDED_DOCUMENT_CLASS = 'is-embedded';

/**
 * Mark the document as embedded before the first paint, so stylesheet rules
 * that must not apply inside somebody else's page never take effect there —
 * chiefly `overscroll-behavior: none`, which would stop a finger that starts on
 * the widget from scrolling the operator's page.
 */
export function applyEmbeddedDocumentFlags(ctx: WidgetBootContext, root = document.documentElement): void {
    root.classList.toggle(EMBEDDED_DOCUMENT_CLASS, Boolean(ctx.isEmbedded));
}

/**
 * The OIDC landing is not a route. Reached without an authorization response to
 * consume — a Back navigation after sign-in, a bookmark, a reload once the
 * params have been stripped — rewrite it to the tenant home before Angular
 * reads the URL, so the router is never asked for `/auth-callback`.
 *
 * A real callback still carries `state`; that one is left alone for
 * `AuthorizationService` to complete and clean up itself.
 */
export function normalizeAuthCallbackLanding(ctx: WidgetBootContext): void {
    if (!ctx.envPath || !ctx.slug) return;
    const url = new URL(window.location.href);
    if (!url.pathname.endsWith(`/${AUTH_CALLBACK_SEGMENT}`)) return;
    if (url.searchParams.has('state')) return;
    url.pathname = url.pathname.slice(0, -AUTH_CALLBACK_SEGMENT.length);
    window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
}

/**
 * Tenant configuration for the booking app, from Product's anonymous booker
 * read. `environment.baseApiUrl` is the Product API base for this build
 * configuration — absolute for every embed bundle, `{host}`-templated for
 * `ng serve` and the `/customer` build, which `createUrl` resolves.
 *
 * Returns null for 404 — an unknown or inactive tenant, or an embed that is
 * switched off — which the caller turns into the unknown-tenant screen without
 * making any further request. A failed read falls back to a configuration
 * cached for this slug within the last 24 hours.
 */
export async function fetchTenantConfig(
    slug: string,
    envPath: string,
    apiBaseUrl = createUrl(environment.baseApiUrl)
): Promise<TenantConfigResponse | null> {
    const apiBase = apiBaseUrl.replace(/\/$/, '');
    const url = `${apiBase}/v1/public/tenants/${encodeURIComponent(slug)}/booker`;
    // Scope the cache to envPath so /embed/dev, /embed/staging and /embed/v1
    // (which share the embed origin's localStorage) don't reuse each other's
    // tenant config during a Product outage.
    const cacheKey = `booker_config:${envPath}${slug}`;

    try {
        const response = await fetch(url);
        if (response.status === 404) {
            // The tenant is gone, inactive or its embed is off. A cached config
            // for this slug is stale in a way no later failure should resurrect.
            try { localStorage.removeItem(cacheKey); } catch { /* unavailable */ }
            return null;
        }
        if (!response.ok) {
            throw new Error(`Booker config HTTP ${response.status}`);
        }
        const data: TenantConfigResponse = await response.json();
        try {
            localStorage.setItem(cacheKey, JSON.stringify({ data, ts: Date.now() }));
        } catch { /* storage full or blocked — non-fatal */ }
        return data;
    } catch (err) {
        console.warn('[web-customer-app] Booker config fetch failed, checking cache:', err);
        let cached: string | null = null;
        try {
            cached = localStorage.getItem(cacheKey);
        } catch { /* third-party storage can be unavailable */ }
        if (cached) {
            try {
                const { data, ts } = JSON.parse(cached);
                if (Date.now() - ts < CONFIG_CACHE_TTL_MS) {
                    console.log('[web-customer-app] Using cached booker config for', slug);
                    return data;
                }
            } catch { /* corrupt cache entry — ignore */ }
        }
        return null;
    }
}
